import {
  LeagueConfig,
  Environment,
  LEAGUE_MODULE_NAMES,
} from "@leagueplatform/core";
import { buildIntlProps, getCurrentLocale } from "@leagueplatform/locales";
import { createReactRouterAdapter } from "@leagueplatform/react-router-adapter";
import { ServiceDetailsBanner } from "components/service-details-banner/service-details-banner.component";
import { APP_ID, AUTH0_CONNECTION, TENANT_ID } from "common/constants";
import { APP_MANAGED_ROUTES } from "common/app-managed-routes";
import { ASSET_OVERRIDES } from "common/asset-overrides";
import EN_LOCALES from "locales/en.json";
import ES_LOCALES from "locales/es-US.json";
import ZH_LOCALES from "locales/zh.json";

import { emblemHealthTheme } from "theme/theme";

export const routesConfig = {
  [LEAGUE_MODULE_NAMES.benefits]: APP_MANAGED_ROUTES.BENEFITS,
  [LEAGUE_MODULE_NAMES.careDocuments]: APP_MANAGED_ROUTES.DOCUMENTS,
  [LEAGUE_MODULE_NAMES.aboutMe]: APP_MANAGED_ROUTES.MY_HEALTH_DASHBOARD,
};

const {
  VITE_APP_ENV,
  VITE_AUTH0_DOMAIN,
  VITE_AUTH0_CLIENT_ID,
  VITE_AUTH0_AUDIENCE,
  VITE_API_URL,
  VITE_WS_API_URL,
  VITE_WS_URL,
  VITE_REST_URL,
  VITE_CONTENT_URL,
} = import.meta.env;

const hasAuth0Config = Boolean(VITE_AUTH0_DOMAIN && VITE_AUTH0_CLIENT_ID);

// Fall back to a no-op auth shape when the env vars aren't present so the
// marketing microsite keeps building.
const authConfig = hasAuth0Config
  ? {
      clientOptions: {
        domain: VITE_AUTH0_DOMAIN!,
        client_id: VITE_AUTH0_CLIENT_ID!,
        audience: VITE_AUTH0_AUDIENCE,
        redirect_uri: window.location.origin,
        connection: AUTH0_CONNECTION,
      },
      // EMBLEM-7075: 15 min matches GHP (Geisinger Health Plan) on web,
      // confirmed by Product (Lindsey Cornwell) as the standard "logout
      // after 15 min of inactivity/background" value already in use for
      // League healthcare clients. Session token itself lives for 60
      // days; this timeout only governs the inactivity boot.
      idleSessionLifetimeHours: 15 / 60,
      sessionTimeoutOptions: {
        // Route through /sign-out. Calling StandaloneAuth.client.logout()
        // here directly is dropped by the SDK — it fires-and-forgets the
        // async callback while closing the modal, and the redirect gets
        // cancelled so the user lands back on /home still authenticated.
        onSessionExpire: () => {
          window.location.assign(APP_MANAGED_ROUTES.SIGN_OUT);
        },
      },
    }
  : {
      getToken: () => Promise.resolve(""),
      getUserIdFromMobileHandoff: () => Promise.resolve(""),
    };

export const config: LeagueConfig = {
  core: {
    tenantId: TENANT_ID,
    clientId: APP_ID,
    auth: authConfig,
    api: {
      url: VITE_API_URL ?? "",
      wsUrl: VITE_WS_API_URL || VITE_WS_URL || "",
      legacyRestApi: VITE_REST_URL ?? "",
    },
    appEnvironment: VITE_APP_ENV as Environment,
    contentUrl: VITE_CONTENT_URL ?? "",
    routing: {
      adapter: createReactRouterAdapter(),
      handleExternalLink: async (url: string, target?: string) => {
        window.open(url, target ?? "_blank", "noreferrer");
      },
    },
    ui: {
      theme: emblemHealthTheme,
      components: {
        pageHeader: {
          shape: "line",
        },
      },
    },
    i18n: {
      strings: {
        default: EN_LOCALES,
        en: EN_LOCALES,
        es: ES_LOCALES,
        zh: ZH_LOCALES,
      },
    },
  },
  assets: {
    overrides: ASSET_OVERRIDES,
  },
  benefits: {
    benefitsLandingMasonryConfigKey: "benefits$landingPage$root",
    benefitsDetailsMasonryConfigKey: "benefits$detailPage$root",
  },
  messaging: {
    // The new message questionnaire already has a "Go back" button, so the
    // close (X) button in its corner is a second control with the same outcome.
    // Design agreed to keep one way out (EMBLEM-7761).
    showNewMessageCloseButton: false,
  },
  careDocuments: {
    // Emblem has no member upload capability.
    isDocumentUploadEnabled: false,
    // Plan Documents keeps the generic "No documents here yet" heading and only
    // swaps the body copy. Any other tab falls back to the generic
    // DOCUMENTS_EMPTY_STATE_HEADING/DESCRIPTION keys the SDK renders when the
    // callback returns `undefined`. Tab key matches the backend
    // `plan_documents` filter key (same as the Android app).
    //
    // SDK contract requires FINAL translated strings, not locale keys — see
    // `CareDocumentsEmptyStateCopy` on `@leagueplatform/config`. Resolving via
    // `buildIntlProps(getCurrentLocale())` keeps the lookup locale-aware (it
    // re-runs on each empty-state render, which fires at most once per tab
    // change) and uses the same message pool the SDK registers, so we don't
    // maintain a second intl instance.
    emptyStateCopyForCategory: (tabKey) => {
      if (tabKey !== "plan_documents") return undefined;
      const { messages } = buildIntlProps(getCurrentLocale());
      return {
        heading: messages.DOCUMENTS_EMPTY_STATE_HEADING ?? "",
        body: messages.DOCUMENTS_EMPTY_STATE_PLAN_DESCRIPTION ?? "",
      };
    },
  },
  // `enableTabFilters` switches /claims to the v2 code path — the Emblem BE
  // connector 501s on the deprecated v1 endpoint, so this flag is required
  // for /claims to work at all.
  claims: {
    featureFlags: {
      shouldShowClaimDetails: true,
      shouldShowDownloadClaims: false,
      shouldShowFilterChips: false,
    },
    showClaimStatusDefinitions: false,
    // iOS parity (EMBLEM-2616): the "Additional Resources" section on the
    // Claim Detail page isn't in Figma. iOS gates it off with this same
    // flag (default false); web SDK uses this flag for Detail page only —
    // Summary page's "Helpful Links" sidebar is still rendered
    // unconditionally by the SDK, hidden by `useHideClaimsHelpfulLinks`.
    showTotalCostOverviewAndAdditionalResources: false,
    showHealthInsuranceGlossary: false,
    enableTabFilters: true,
    slots: {
      ServiceDetailsBanner,
    },
  },
  healthJourney: {
    headerConfig: {
      disableBackgroundImage: true,
      backgroundColor: "$surfaceBackgroundTertiary",
    },
  },
  // Required so Radix-driven accordions in SDK modules actually toggle open
  // — without these flags aria-expanded flips but the panel stays hidden.
  __internal: {
    shouldApplyCSSReset: true,
    shouldApplyLegacyGlobalStyles: true,
    shouldApplyActivityLayoutFixes: true,
    shouldIgnoreDOMLangAttributeForLocale: true,
  },
};
